import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PageShell from "@/components/layout/PageShell";
import ServerPagination from "@/components/ServerPagination";
import LogReportDialog from "@/components/LogReportDialog";
import LogItemsEditor, { emptyItem, allRowsDecided, isRowDecided } from "@/components/LogItemsEditor";
import ItemsReportDialog from "@/components/ItemsReportDialog";
import { PackagePlus, Plus, X, CheckCircle2, Circle, ArrowRight, Pencil, Trash2, ClipboardList, FileDown, ScanLine, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import CourierSelect from "@/components/CourierSelect";

const EMPTY = { mode: "Courier", courier: "", shipment_count: 0, delivery_staff_name: "", signature_data_url: "", notes: "", item_type: "Parcel", parcel_condition: "Good", items: [] };
const LSKEY = "ilog.pageSize";

// Detect courier from a scanned barcode/AWB. Feb 12, 2026 — surgical OCR/scan
// bootstrap; falls back to first prefix-match against the tenant's courier
// master when no well-known rule fires.
const COURIER_RULES = [
  { re: /^BD/i, name: "BlueDart" },
  { re: /^DTDC/i, name: "DTDC" },
  { re: /^(SF|SDL|SFX)/i, name: "Shadowfax" },
  { re: /^(DEL|DL)/i, name: "Delhivery" },
  { re: /^EK/i, name: "Ekart" },
  { re: /^(FDX|FX|FEDEX)/i, name: "FedEx" },
  { re: /^(DHL)/i, name: "DHL" },
  { re: /^\d{13,}$/, name: "IndiaPost" },
];
const detectCourierFromCode = (code, couriers) => {
  const c = (code || "").trim().toUpperCase();
  if (!c) return "";
  for (const r of COURIER_RULES) {
    if (r.re.test(c)) {
      const m = couriers.find((x) => (x.name || "").toLowerCase() === r.name.toLowerCase());
      if (m) return m.name;
    }
  }
  const pfxMatch = couriers.find((x) => {
    const n = (x.name || "").replace(/\s+/g, "").toUpperCase();
    return n && n.length >= 2 && c.startsWith(n.slice(0, 2));
  });
  return pfxMatch?.name || "";
};

export default function InwardLogPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem(LSKEY) || "50", 10));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveAndNew, setSaveAndNew] = useState(false);
  const firstFieldRef = useRef(null);
  const scanRef = useRef(null);
  const [reportLog, setReportLog] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [manifestLog, setManifestLog] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanFlash, setScanFlash] = useState(null); // {type, text}
  const [couriers, setCouriers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const ocrRef = useRef(null);
  const [scanPrompt, setScanPrompt] = useState(false);
  const prevLastDecidedRef = useRef(false);
  const activeStaff = useMemo(() => staffList.filter((s) => s.active !== false), [staffList]);

  // Post-decision prompt (Feb 12, 2026) — as soon as the LAST item row flips
  // from undecided → decided (ACCEPT, RTO with reason, or Override with reason),
  // pop a "Scan another item?" nudge with Yes / No.
  useEffect(() => {
    const list = form.items || [];
    if (list.length === 0) { prevLastDecidedRef.current = false; setScanPrompt(false); return; }
    const lastDecided = isRowDecided(list[list.length - 1]);
    if (lastDecided && !prevLastDecidedRef.current) setScanPrompt(true);
    prevLastDecidedRef.current = lastDecided;
  }, [form.items]);

  const load = async (nextPage = page, nextSize = pageSize) => {
    try {
      const { data } = await api.get("/inward-logs", { params: { page: nextPage, page_size: nextSize } });
      setItems(data.items || []);
      setTotal(data.total || 0); setPages(data.pages || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);

  // Deep-link from ScanPage "Inward Log Scan" card → auto-open the new log dialog.
  useEffect(() => {
    if (searchParams.get("new") === "1" && !open) {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Load masters when the New-Log dialog opens (courier detect + staff datalist).
  useEffect(() => {
    if (!open || editingId) return;
    api.get("/couriers").then((r) => setCouriers(r.data || [])).catch(() => {});
    api.get("/delivery-staff").then((r) => setStaffList(r.data || [])).catch(() => {});
    setTimeout(() => scanRef.current?.focus?.(), 60);
  }, [open, editingId]);

  // Scan handler — parses a scanned code, detects courier via master list, adds
  // a new item row with the code as the identifier and bumps shipment_count.
  const handleScan = (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    // Duplicate guard — same tracking number cannot be scanned twice in one log.
    const dup = (form.items || []).some((it) => (it.name || "").trim().toUpperCase() === code.toUpperCase());
    if (dup) {
      setScanCode("");
      setScanFlash({ type: "warn", text: `Duplicate · ${code} already in this log` });
      toast.error(`Duplicate scan blocked · ${code}`);
      setTimeout(() => setScanFlash(null), 3000);
      setTimeout(() => scanRef.current?.focus?.(), 30);
      return;
    }
    const detected = detectCourierFromCode(code, couriers);
    setForm((f) => {
      const nextItems = [...(f.items || [])];
      // Reuse the last row if it's still blank; otherwise append a new one.
      const last = nextItems[nextItems.length - 1];
      if (last && !last.name && !last.recipient_id) {
        nextItems[nextItems.length - 1] = { ...last, name: code };
      } else {
        nextItems.push({ ...emptyItem(), name: code });
      }
      return {
        ...f,
        courier: f.courier || detected,   // don't overwrite an operator's manual choice
        items: nextItems,
        shipment_count: Math.max(0, Number(f.shipment_count) || 0) + (last && !last.name ? 0 : 1),
      };
    });
    setScanCode("");
    setScanPrompt(false); // dismiss any pending nudge — new item is being added
    setScanFlash({ type: detected ? "ok" : "warn",
                    text: detected ? `Scanned · courier auto-set to ${detected}` : `Scanned · courier not recognised — set manually` });
    setTimeout(() => setScanFlash(null), 2500);
    scanRef.current?.focus?.(); // instant refocus for burst scanning
  };

  // OCR label — snap/upload a photo, extract courier + AWB + recipient via Gemini
  // vision, pre-fill the form. Reuses the existing /ai/scan-label endpoint.
  const handleOcrFile = async (file) => {
    if (!file) return;
    setOcrBusy(true);
    setScanFlash({ type: "warn", text: "Reading label…" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/scan-label", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.extracted || {};
      const m = x.matched_recipient;
      const awb = (x.awb_number || x.tracking_number || "").trim();
      // Duplicate guard — same AWB can't be added twice via OCR either.
      if (awb) {
        const dup = (form.items || []).some((it) => (it.name || "").trim().toUpperCase() === awb.toUpperCase());
        if (dup) {
          setScanFlash({ type: "warn", text: `Duplicate · ${awb} already in this log` });
          toast.error(`Duplicate label blocked · ${awb}`);
          return;
        }
      }
      setForm((f) => {
        const nextItems = [...(f.items || [])];
        const last = nextItems[nextItems.length - 1];
        const seed = {
          ...emptyItem(),
          name: awb || last?.name || "",
          recipient_id: m?.id || "",
          recipient_name: m?.name || x.recipient_name || "",
          recipient_department: m?.department || "",
          recipient_status: m ? "active" : null,
          decision: "", // operator confirms ACCEPT / RTO / Override
        };
        const reuse = last && !last.name && !last.recipient_id;
        if (reuse) nextItems[nextItems.length - 1] = seed;
        else       nextItems.push(seed);
        return {
          ...f,
          courier: f.courier || x.courier || "",
          items: nextItems,
          shipment_count: Math.max(0, Number(f.shipment_count) || 0) + (reuse ? 0 : 1),
        };
      });
      const bits = [];
      if (x.courier) bits.push(x.courier);
      if (awb) bits.push(`AWB ${awb}`);
      if (m) bits.push(`→ ${m.name}`);
      setScanFlash({ type: "ok",
        text: `Label read · ${bits.join(" · ") || "no fields recognised"}${x.handwritten ? " (handwritten)" : ""}` });
      toast.success("Label extracted", { description: bits.join(" · ") || "Review fields before saving" });
    } catch (e) {
      setScanFlash({ type: "warn", text: "OCR failed — try a clearer photo" });
      toast.error(e?.response?.data?.detail || "AI scan failed");
    } finally {
      setOcrBusy(false);
      if (ocrRef.current) ocrRef.current.value = ""; // allow re-selecting same file
      setTimeout(() => setScanFlash(null), 4000);
    }
  };

  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      mode: l.mode || "Courier",
      courier: l.courier || "",
      shipment_count: l.shipment_count || 1,
      delivery_staff_name: l.delivery_staff_name || "",
      signature_data_url: l.signature_data_url || "",
      notes: l.notes || "",
    });
    setOpen(true);
  };

  const deleteLog = async (l) => {
    if (!window.confirm(`Delete log entry for ${l.delivery_staff_name}? Parcels already logged against it are kept.`)) return;
    try {
      await api.delete(`/inward-logs/${l.id}`);
      toast.success("Log deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.delivery_staff_name) return toast.error("Delivery-staff name is required");
    if (!editingId && !form.signature_data_url) return toast.error("Please sign to accept the delivery");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/inward-logs/${editingId}`, {
          mode: form.mode, courier: form.courier || "",
          shipment_count: Math.max(1, Number(form.shipment_count) || 1),
          delivery_staff_name: form.delivery_staff_name, notes: form.notes,
        });
        toast.success("Log updated · progress recalculated");
        setOpen(false); setEditingId(null); setForm(EMPTY); await load();
      } else {
        const { data } = await api.post("/inward-logs", { ...form, shipment_count: Math.max(1, Number(form.shipment_count) || 1) });
        toast.success(`Log saved · ${data.shipment_count} shipment(s)`);
        if (saveAndNew) {
          setSaveAndNew(false);
          setForm(EMPTY);
          await load();
          setTimeout(() => firstFieldRef.current?.focus?.(), 30);
        } else {
          setOpen(false); setForm(EMPTY); await load();
          nav(`/app/inward?log_id=${data.id}`);
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageShell
      title="Inward Log."
      overline={<><PackagePlus size={12}/> Inward · reception log</>}
      subtitle="Log the courier handover first, then create individual inward entries against it. Progress is tracked automatically."
      testid="ilog"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="ilog-report" onClick={() => setReportOpen(true)} variant="outline"
                  className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
            <FileDown size={14} className="mr-2" /> Report
          </Button>
          <Button data-testid="ilog-new" onClick={() => setOpen(true)} className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
            <Plus size={14} className="mr-2" /> New log entry
          </Button>
        </div>
      }
    >

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border overline text-left">
              <th className="p-4">When</th><th className="p-4">Mode</th><th className="p-4">Courier</th>
              <th className="p-4">Delivery Staff</th><th className="p-4">Count</th>
              <th className="p-4">Progress</th><th className="p-4">Status</th><th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody data-testid="ilog-table">
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No log entries yet. Create one when a courier drops a batch of parcels.</td></tr>
            )}
            {items.map((l) => {
              const rto = l.intake_rto_count || 0;
              const total = Math.max(1, (l.shipment_count || 1) - rto);
              const done = l.entries_done || 0;
              const pct = Math.min(100, Math.round((done / total) * 100));
              return (
                <tr key={l.id} className="border-b border-border hover:bg-muted/40" data-testid={`ilog-row-${l.id}`}>
                  <td className="p-4 mono text-xs">{(l.created_at || "").slice(0,16).replace("T"," ")}</td>
                  <td className="p-4"><span className="text-[10px] uppercase tracking-widest font-bold border border-border px-1.5 py-0.5 rounded">{l.mode}</span></td>
                  <td className="p-4">{l.courier || "—"}</td>
                  <td className="p-4">{l.delivery_staff_name}</td>
                  <td className="p-4 mono text-xs">
                    {l.shipment_count || 1}
                    {rto > 0 && <span className="ml-1 text-[9px] uppercase tracking-widest text-red-700 font-bold" data-testid={`ilog-rto-badge-${l.id}`}>· {rto} RTO</span>}
                  </td>
                  <td className="p-4 w-48">
                    <div className="flex items-center gap-2 mono text-xs" data-testid={`ilog-progress-${l.id}`}>
                      <span>{done} / {total}</span>
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {l.status === "completed"
                      ? <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1"><CheckCircle2 size={10}/> Completed</span>
                      : <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1"><Circle size={10}/> Open</span>}
                  </td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => nav(`/app/inward?log_id=${l.id}`)} data-testid={`ilog-open-${l.id}`} className="rounded-md">
                      Log entry <ArrowRight size={12} className="ml-1" />
                    </Button>
                    <button type="button" onClick={() => openEdit(l)} data-testid={`ilog-edit-${l.id}`}
                      className="ml-2 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Edit log">
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => setManifestLog(l)} data-testid={`ilog-manifest-${l.id}`}
                      className="ml-1 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Manifest">
                      <FileDown size={13} />
                    </button>
                    <button type="button" onClick={() => setReportLog(l)} data-testid={`ilog-items-${l.id}`}
                      className="ml-1 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Items report">
                      <ClipboardList size={13} />
                    </button>
                    <button type="button" onClick={() => deleteLog(l)} data-testid={`ilog-delete-${l.id}`}
                      className="ml-1 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Delete log">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem(LSKEY, String(s)); setPage(1); }}
        storageKey={LSKEY} testidPrefix="ilog-pg"
      />

      <LogReportDialog open={reportOpen} onClose={() => setReportOpen(false)} direction="inward" />
      <LogReportDialog open={!!manifestLog} onClose={() => setManifestLog(null)} direction="inward" logId={manifestLog?.id} manifestMode={true} />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="rounded-md max-w-lg p-0 flex flex-col max-h-[85vh] gap-0" data-testid="ilog-create-dialog">
          <form onSubmit={save} className="flex flex-col flex-1 min-h-0">
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit inward log entry" : "New inward log entry"}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0 pb-8">
            {!editingId && (
              <div className="border-2 border-primary/40 bg-red-50/30 rounded-md p-3" data-testid="ilog-scan-block">
                <label className="block">
                  <span className="overline flex items-center gap-1.5"><ScanLine size={11} className="text-primary"/> Scan barcode / AWB</span>
                  <div className="mt-1 flex gap-2">
                    <Input
                      data-testid="ilog-scan-input"
                      ref={scanRef}
                      value={scanCode}
                      onChange={(e) => setScanCode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleScan(scanCode); } }}
                      placeholder="Scan or paste code, press Enter…"
                      className="rounded-md font-mono text-sm h-10 bg-white flex-1"
                      autoComplete="off"
                    />
                    <input
                      ref={ocrRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      data-testid="ilog-ocr-input"
                      onChange={(e) => handleOcrFile(e.target.files?.[0])}
                      className="hidden"
                    />
                    <button
                      type="button"
                      data-testid="ilog-ocr-btn"
                      disabled={ocrBusy}
                      onClick={() => ocrRef.current?.click()}
                      title="Snap or upload a photo of the shipping label — AI will pre-fill courier and recipient"
                      className="h-10 px-3 rounded-md border border-primary/50 bg-primary/5 text-primary font-semibold text-xs inline-flex items-center gap-1.5 hover:bg-primary hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Camera size={13}/> {ocrBusy ? "Reading…" : "Snap label"}
                    </button>
                  </div>
                </label>
                {scanFlash && (
                  <div data-testid="ilog-scan-flash"
                       className={`mt-2 text-[11px] font-semibold flex items-center gap-1 ${scanFlash.type === "ok" ? "text-emerald-700" : "text-amber-700"}`}>
                    {scanFlash.text}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  Each scan appends an item row and auto-increments <b>No. of shipments</b>. <b>Snap label</b> uses AI OCR to pre-fill courier + recipient from a photo. Recipient status is checked per row after you pick from the directory.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline">Mode</span>
                <select data-testid="ilog-mode" value={form.mode} onChange={e => setForm({...form, mode: e.target.value})}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option>Courier</option><option>Postal</option><option>Intercompany</option>
                </select>
              </label>
              <label className="block">
                <span className="overline">Courier</span>
                <div className="mt-1"><CourierSelect value={form.courier} onChange={v => setForm({...form, courier: v})} testid="ilog-courier" /></div>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline">No. of shipments *</span>
                <Input data-testid="ilog-count" ref={firstFieldRef} type="number" min="0" value={form.shipment_count}
                  onChange={e => setForm({...form, shipment_count: e.target.value})} className="mt-1 rounded-md font-mono" required />
              </label>
              <label className="block">
                <span className="overline">Delivery-staff name *</span>
                <Input data-testid="ilog-staff" value={form.delivery_staff_name}
                  onChange={e => setForm({...form, delivery_staff_name: e.target.value})}
                  placeholder="Type or pick from active staff"
                  list="ilog-staff-suggest"
                  className="mt-1 rounded-md" required />
                <datalist id="ilog-staff-suggest">
                  {activeStaff.map((s) => (
                    <option key={s.id} value={s.name}>{s.phone ? `${s.name} · ${s.phone}` : s.name}</option>
                  ))}
                </datalist>
              </label>
            </div>
              <label className="block">
                <span className="overline">Notes</span>
                <Input data-testid="ilog-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-md" placeholder="Optional" />
              </label>
              <LogItemsEditor value={form.items || []} onChange={(items) => setForm({...form, items})} testid="ilog-items" variant="in"
                onItemAdded={() => setForm((f) => ({ ...f, shipment_count: Math.max(0, Number(f.shipment_count) || 0) + 1 }))} />
            {!editingId && scanPrompt && (
              <div className="border-2 border-emerald-500 bg-emerald-50/70 rounded-md p-3 flex items-center gap-3 mt-2" data-testid="ilog-scan-again-prompt">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-900">Scan another item?</div>
                  <div className="text-[11px] text-emerald-800/80 mt-0.5">Last item is marked. Ready for the next scan, or review & save.</div>
                </div>
                <button type="button" data-testid="ilog-scan-again-yes"
                  onClick={() => { setScanPrompt(false); scanRef.current?.focus?.(); ocrRef.current?.click?.(); }}
                  className="h-8 px-3 text-xs font-bold uppercase tracking-widest rounded border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                  Yes · Scan
                </button>
                <button type="button" data-testid="ilog-scan-again-no"
                  onClick={() => setScanPrompt(false)}
                  className="h-8 px-3 text-xs font-bold uppercase tracking-widest rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-100 transition-colors">
                  No · Review
                </button>
              </div>
            )}
            <div>
              <span className="overline">Digital signature to accept delivery {editingId ? <span className="text-muted-foreground normal-case tracking-normal">(locked while editing)</span> : "*"}</span>
              {editingId
                ? <div className="mt-1 h-16 border border-dashed border-border rounded-md flex items-center justify-center text-xs text-muted-foreground">Signature preserved — cannot be re-drawn during edit.</div>
                : <div className="mt-1"><SignaturePad value={form.signature_data_url} onChange={v => setForm({...form, signature_data_url: v})} testid="ilog-signature" /></div>
              }
            </div>
            </div>
            <div className="shrink-0 px-6 py-3 bg-white border-t border-border flex justify-end gap-2">
              {!editingId && !allRowsDecided(form.items) && (form.items || []).length > 0 && (
                <div className="mr-auto text-[11px] text-muted-foreground self-center" data-testid="ilog-gate-msg">
                  Decide every item (Accept a user or mark RTO) before saving.
                </div>
              )}
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditingId(null); setForm(EMPTY); }} data-testid="ilog-cancel" className="rounded-md"><X size={13} className="mr-1"/> Cancel</Button>
              {!editingId && (
                <Button type="submit" onClick={() => setSaveAndNew(true)}
                        disabled={busy || (!editingId && !allRowsDecided(form.items))}
                        data-testid="ilog-save-new" variant="outline"
                        className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
                  <Plus size={13} className="mr-1"/> Save &amp; New
                </Button>
              )}
              <Button type="submit" onClick={() => setSaveAndNew(false)}
                      disabled={busy || (!editingId && !allRowsDecided(form.items))}
                      data-testid="ilog-submit"
                      className="rounded-md bg-primary text-primary-foreground">
                {busy ? "Saving…" : (editingId ? "Save changes" : "Save & start entries")} <ArrowRight size={13} className="ml-1" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <ItemsReportDialog log={reportLog} open={!!reportLog} onClose={() => setReportLog(null)} />
    </PageShell>
  );
}
